import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { QUERY_KEYS } from '@/modules/shared/constants/query-keys.contant'
import { useResolveRequestHook } from '@/modules/shared/hooks/use-resolve-request.hook'
import { useMainStore } from '@/modules/shared/stores/main.store'

import {
  useOrderFormComponentStore,
  useOrderFormComponentStoreActions,
} from '../../stores/order-form-component.store'
import {
  useOrderFormStore,
  useOrderFormStoreActions,
} from '../../stores/order-form.store'
import { useOrderFormClearData } from '../../hooks/use-order-form-clear-data.hook'
import useOrderFormParams from '../../hooks/use-order-form-params.hook'
import { ORDER_FORM_RESUME } from './summary.constant'
import { useSaveOrderMutation } from '../../mutations/save-order.mutation'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const updateFieldsGroup = (defaultGroup: any[], layoutGroup: any[]) => {
  return defaultGroup.map((defaultItem) => {
    const foundItem = layoutGroup.find(
      (layoutItem) => layoutItem.campo === defaultItem.campo,
    )
    if (foundItem) {
      return {
        ...defaultItem,
        label: foundItem.label || defaultItem.label,
        enabled: foundItem.enabled ?? defaultItem.enabled,
        visible: foundItem.visible ?? defaultItem.visible,
      }
    }
    return defaultItem
  })
}

const { setOpenModalScheduleAfterSave } = useOrderFormComponentStoreActions

export const useSummary = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const {
    initialLocationForOrder,
    actions: { setInitialLocationForOrder },
  } = useMainStore()

  const { jsonOrderForm, isCalculationOrder, paramsLayoutOrderForm } =
    useOrderFormStore((state) => ({
      jsonOrderForm: state.jsonOrderForm,
      operationType: state.operationType,
      isCalculationOrder: state.isCalculationOrder,
      paramsLayoutOrderForm: state.paramsLayoutOrderForm,
    }))

  const { clearAllDataOfOrder } = useOrderFormClearData()

  const { isViewOrder, isVisibleSignature } = useOrderFormParams()

  const form = useForm({
    defaultValues: {
      observation: jsonOrderForm?.obsPedido,
    },
  })

  const { openModalScheduleAfterSave } = useOrderFormComponentStore()

  const { setIsOpenModalSignatureOrder, setIsOpenModalSchedule } =
    useOrderFormComponentStoreActions

  const callbackToRequest = async () => {
    if (isVisibleSignature) {
      setIsOpenModalSignatureOrder(true)
      return
    }

    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.ORDERS],
    })
    queryClient.removeQueries({
      queryKey: ['orderForm', 'new'],
    })
    await queryClient.invalidateQueries([QUERY_KEYS.ORDER_TRANSMISSION])

    if (openModalScheduleAfterSave) {
      setOpenModalScheduleAfterSave(false)
      setIsOpenModalSchedule(true)
      return
    }

    clearAllDataOfOrder()

    if (initialLocationForOrder) {
      setInitialLocationForOrder(undefined)
      navigate(initialLocationForOrder)
    } else {
      navigate('/pedidos')
    }
  }

  const { checkStatusOnRequest } = useResolveRequestHook()
  const { saveOrderFn, isSavingOrder } = useSaveOrderMutation({
    callback: callbackToRequest,
    resolveRequest: checkStatusOnRequest,
  })

  const handleSaveOrder = async () => {
    if (jsonOrderForm?.itemsCalculo?.length <= 0) {
      toast.info('Por favor, inclua um produto para finalizar!')
      return
    }

		if (isVisibleSignature) {
      setIsOpenModalSignatureOrder(true)
      return
    }

    await saveOrderFn({ jsonOrderForm })
  }

  const { setIsCalculationOrder, setJsonOrderForm } = useOrderFormStoreActions()

  const handleChangeValues = (fieldInJson: string, valueToInsert: unknown) => {
    if (!isCalculationOrder) setIsCalculationOrder(true)

    const newJsonOrderForm = {
      ...jsonOrderForm,
      [fieldInJson]: valueToInsert,
    }

    setJsonOrderForm(newJsonOrderForm)
  }

  const fieldsGroup = useMemo(() => {
    const defaultFieldsGroup1 = updateFieldsGroup(
      ORDER_FORM_RESUME.group1,
      paramsLayoutOrderForm?.finalizar || [],
    )
    const defaultFieldsGroup2 = updateFieldsGroup(
      ORDER_FORM_RESUME.group2,
      paramsLayoutOrderForm?.finalizar || [],
    )
    const defaultFieldsGroup3 = updateFieldsGroup(
      ORDER_FORM_RESUME.group3,
      paramsLayoutOrderForm?.finalizar || [],
    )

    return {
      defaultFieldsGroup1,
      defaultFieldsGroup2,
      defaultFieldsGroup3,
    }
  }, [paramsLayoutOrderForm])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return {
    isViewOrder,
    jsonOrderForm,
    isSavingOrder,
    form,
    handleSaveOrder,
    handleChangeValues,
    fieldsGroup,
    setOpenModalScheduleAfterSave,
  }
}
